import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  CSPMService,
  type IAttackPathQueryResult,
} from "@/src/services/CSPM/cspmService";

export type AttackPathSyncState = "idle" | "syncing" | "synced" | "error";

interface AttackPathSyncData {
  sync_id: string;
  sync_tag: number;
  account_id: string;
  completed_at: string;
}

interface AttackPathQueryData {
  query: string;
  description: string;
  sync_tag: number;
  results: IAttackPathQueryResult[];
}

export interface CSPMAttackPathState {
  activeAccountId: string | null;
  activeAccountLabel: string | null;
  startedAt: number | null;

  syncState: AttackPathSyncState;
  syncError: string | null;
  syncData: AttackPathSyncData | null;

  isLoadingQueries: boolean;
  queries: Record<string, string>;
  queriesError: string | null;
  selectedQuery: string;

  isExecutingQuery: boolean;
  queryResult: AttackPathQueryData | null;
  queryError: string | null;

  syncNotified: boolean;
}

const initialState: CSPMAttackPathState = {
  activeAccountId: null,
  activeAccountLabel: null,
  startedAt: null,

  syncState: "idle",
  syncError: null,
  syncData: null,

  isLoadingQueries: false,
  queries: {},
  queriesError: null,
  selectedQuery: "",

  isExecutingQuery: false,
  queryResult: null,
  queryError: null,

  syncNotified: true,
};

const extractErrorMessage = (error: any, fallback: string): string => {
  return (
    error?.response?.data?.detail?.message ||
    (typeof error?.response?.data?.detail === "string" ? error.response.data.detail : null) ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
};

export const syncAttackPathEnv = createAsyncThunk(
  "cspmAttackPath/syncEnv",
  async (
    args: { accountId: string; accountLabel?: string | null },
    { rejectWithValue }
  ) => {
    try {
      const response = await CSPMService.syncAttackPath(args.accountId);
      if (response?.success) {
        return response.data as AttackPathSyncData;
      }
      return rejectWithValue(response?.message || "Attack path sync failed");
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error, "Attack path sync failed"));
    }
  }
);

export const fetchAttackPathQueries = createAsyncThunk(
  "cspmAttackPath/fetchQueries",
  async (_, { rejectWithValue }) => {
    try {
      const response = await CSPMService.getAttackPathQueries();
      if (response?.success && response?.data) {
        return response.data as Record<string, string>;
      }
      return rejectWithValue(response?.message || "Failed to fetch queries");
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error, "Failed to fetch queries"));
    }
  }
);

export const executeAttackPathQuery = createAsyncThunk(
  "cspmAttackPath/executeQuery",
  async (
    args: { queryName: string; accountId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await CSPMService.runAttackPathQuery(args.queryName, args.accountId);
      if (response?.success && response?.data) {
        return response.data as AttackPathQueryData;
      }
      return rejectWithValue(response?.message || "Query execution failed");
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error, "Query execution failed"));
    }
  }
);

const cspmAttackPathSlice = createSlice({
  name: "cspmAttackPath",
  initialState,
  reducers: {
    setSelectedQuery: (state, action: PayloadAction<string>) => {
      state.selectedQuery = action.payload;
    },
    markSyncNotified: (state) => {
      state.syncNotified = true;
    },
    resetPerAccountAttackPathState: (state) => {
      state.activeAccountId = null;
      state.activeAccountLabel = null;
      state.startedAt = null;
      state.syncState = "idle";
      state.syncError = null;
      state.syncData = null;
      state.isExecutingQuery = false;
      state.queryResult = null;
      state.queryError = null;
      state.selectedQuery = "";
      state.syncNotified = true;
    },
    resetAllAttackPathState: () => initialState,
  },

  extraReducers: (builder) => {
    builder
      .addCase(syncAttackPathEnv.pending, (state, action) => {
        state.syncState = "syncing";
        state.syncError = null;
        state.syncData = null;
        state.startedAt = Date.now();
        state.activeAccountId = action.meta.arg.accountId;
        state.activeAccountLabel = action.meta.arg.accountLabel ?? action.meta.arg.accountId;
        state.syncNotified = false;
      })
      .addCase(syncAttackPathEnv.fulfilled, (state, action) => {
        state.syncState = "synced";
        state.syncData = action.payload;
      })
      .addCase(syncAttackPathEnv.rejected, (state, action) => {
        state.syncState = "error";
        state.syncError =
          (action.payload as string) || action.error.message || "Attack path sync failed";
      })

      .addCase(fetchAttackPathQueries.pending, (state) => {
        state.isLoadingQueries = true;
        state.queriesError = null;
      })
      .addCase(fetchAttackPathQueries.fulfilled, (state, action) => {
        state.isLoadingQueries = false;
        state.queries = action.payload;
      })
      .addCase(fetchAttackPathQueries.rejected, (state, action) => {
        state.isLoadingQueries = false;
        state.queriesError =
          (action.payload as string) || action.error.message || "Failed to fetch queries";
      })

      .addCase(executeAttackPathQuery.pending, (state) => {
        state.isExecutingQuery = true;
        state.queryError = null;
        state.queryResult = null;
      })
      .addCase(executeAttackPathQuery.fulfilled, (state, action) => {
        state.isExecutingQuery = false;
        state.queryResult = action.payload;
      })
      .addCase(executeAttackPathQuery.rejected, (state, action) => {
        state.isExecutingQuery = false;
        state.queryError =
          (action.payload as string) || action.error.message || "Query execution failed";
      });
  },
});

export const {
  setSelectedQuery,
  markSyncNotified,
  resetPerAccountAttackPathState,
  resetAllAttackPathState,
} = cspmAttackPathSlice.actions;

export default cspmAttackPathSlice.reducer;
